import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Bot, Send, Loader2, Plus, Trash2, CheckCircle2, XCircle,
  BarChart3, Users, FolderKanban, Plane, AlertTriangle, Film,
  Clock, Zap, MessageSquare, ChevronRight, User, Shield, Mic, MicOff,
  Volume2, VolumeX, Square
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';

const API = '/api';

const QuickAction = ({ icon: Icon, label, prompt, onClick, color }) => (
  <button
    onClick={() => onClick(prompt)}
    className={`flex items-center gap-2.5 px-4 py-3 rounded-xl border transition-all hover:shadow-md hover:-translate-y-0.5 text-left ${color}`}
    data-testid={`quick-action-${label.toLowerCase().replace(/\s/g, '-')}`}
  >
    <Icon size={16} className="flex-shrink-0" />
    <span className="text-xs font-medium">{label}</span>
  </button>
);

const ChatMessage = ({ msg, onConfirm, onReject, confirming, onSpeak, playingMsgIdx, ttsLoading, msgIdx }) => {
  const isUser = msg.role === 'user';
  const isSystem = msg.role === 'system';
  const isPlaying = playingMsgIdx === msgIdx;
  const isTtsLoading = ttsLoading === msgIdx;

  return (
    <div className={`flex gap-3 ${isUser ? 'justify-end' : 'justify-start'} animate-fade-in`} data-testid={`chat-msg-${msg.role}`}>
      {!isUser && (
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isSystem ? 'bg-green-100' : 'bg-gradient-to-br from-gold/20 to-gold/5 border border-gold/30'}`}>
          {isSystem ? <CheckCircle2 size={14} className="text-green-600" /> : <Bot size={14} className="text-gold" />}
        </div>
      )}
      <div className={`max-w-[75%] ${isUser ? 'order-first' : ''}`}>
        <div className={`rounded-2xl px-4 py-3 text-sm leading-relaxed ${
          isUser
            ? 'bg-navy text-white rounded-br-md'
            : isSystem
              ? 'bg-green-50 border border-green-200 text-green-800 rounded-bl-md'
              : 'bg-white border border-gray-200 text-gray-800 shadow-sm rounded-bl-md'
        }`}>
          {isUser ? msg.content : (
            <ReactMarkdown components={{
              p: ({children}) => <p className="mb-2 last:mb-0">{children}</p>,
              strong: ({children}) => <strong className="font-semibold">{children}</strong>,
              ul: ({children}) => <ul className="list-disc list-inside mb-2 space-y-0.5">{children}</ul>,
              ol: ({children}) => <ol className="list-decimal list-inside mb-2 space-y-0.5">{children}</ol>,
              li: ({children}) => <li className="text-sm">{children}</li>,
              h3: ({children}) => <h3 className="font-semibold text-sm mb-1 mt-2">{children}</h3>,
            }}>{msg.content}</ReactMarkdown>
          )}
        </div>
        {/* TTS button for assistant messages */}
        {msg.role === 'assistant' && msg.content && (
          <button
            onClick={() => onSpeak(msg.content, msgIdx)}
            disabled={isTtsLoading}
            className={`mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-mono transition-colors ${
              isPlaying ? 'bg-gold/20 text-gold border border-gold/30' : 'bg-gray-100 text-gray-400 hover:text-gold hover:bg-gold/10'
            }`}
            data-testid={`tts-btn-${msgIdx}`}
          >
            {isTtsLoading ? <Loader2 size={9} className="animate-spin" /> : isPlaying ? <Square size={9} /> : <Volume2 size={9} />}
            {isTtsLoading ? 'GERANDO...' : isPlaying ? 'PARAR' : 'OUVIR'}
          </button>
        )}
        {msg.action && !msg.action_result && (
          <div className="mt-2 bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="action-confirmation">
            <p className="text-amber-800 text-xs font-semibold mb-2 flex items-center gap-1.5">
              <AlertTriangle size={12} /> CONFIRMACAO NECESSARIA
            </p>
            <p className="text-amber-700 text-[11px] font-mono mb-3 bg-amber-100/50 rounded-lg p-2">
              {msg.action_description || `${msg.action.type}: ${JSON.stringify(msg.action.params)}`}
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => onConfirm(msg.action)}
                disabled={confirming}
                className="bg-green-600 hover:bg-green-700 text-white text-xs h-8 px-4 rounded-lg"
                data-testid="confirm-action-btn"
              >
                {confirming ? <Loader2 size={12} className="animate-spin mr-1" /> : <CheckCircle2 size={12} className="mr-1" />}
                CONFIRMAR
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={onReject}
                className="text-red-600 border-red-200 hover:bg-red-50 text-xs h-8 px-4 rounded-lg"
                data-testid="reject-action-btn"
              >
                <XCircle size={12} className="mr-1" /> CANCELAR
              </Button>
            </div>
          </div>
        )}
        {msg.action_result && (
          <div className={`mt-2 rounded-xl p-2.5 text-xs ${msg.action_result.success ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
            {msg.action_result.success ? <CheckCircle2 size={12} className="inline mr-1" /> : <XCircle size={12} className="inline mr-1" />}
            {msg.action_result.message}
            {msg.action_result.download_url && (
              <button
                onClick={() => {
                  fetch(msg.action_result.download_url, {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('rpas_token')}` }
                  }).then(res => {
                    if (!res.ok) throw new Error('Download failed');
                    const ext = msg.action_result.format || 'pdf';
                    const ct = res.headers.get('content-disposition');
                    const fname = ct ? ct.split('filename=')[1]?.replace(/"/g, '') : `relatorio_jornada.${ext === 'excel' ? 'xlsx' : ext === 'word' ? 'docx' : ext}`;
                    return res.blob().then(blob => ({ blob, fname }));
                  }).then(({ blob, fname }) => {
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = fname;
                    a.click();
                    window.URL.revokeObjectURL(url);
                  }).catch(() => toast.error('Erro ao baixar relatorio'));
                }}
                className="ml-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-navy text-white font-medium hover:bg-navy/80 transition-colors cursor-pointer"
                data-testid="download-report-link"
              >
                <ChevronRight size={10} /> Baixar {(msg.action_result.format || 'PDF').toUpperCase()}
              </button>
            )}
          </div>
        )}
        <p className="text-gray-300 text-[9px] font-mono mt-1 px-1">
          {msg.timestamp && new Date(msg.timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
        </p>
      </div>
      {isUser && (
        <div className="w-8 h-8 rounded-lg bg-navy/10 flex items-center justify-center flex-shrink-0">
          <User size={14} className="text-navy" />
        </div>
      )}
    </div>
  );
};

const AIAssistantPage = () => {
  const { token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => crypto.randomUUID());
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [showSessions, setShowSessions] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [playingMsgIdx, setPlayingMsgIdx] = useState(null);
  const [ttsLoading, setTtsLoading] = useState(null);
  const [autoSpeak, setAutoSpeak] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const mediaRecRef = useRef(null);
  const audioRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      if (scrollRef.current) {
        const el = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
        if (el) el.scrollTop = el.scrollHeight;
      }
    }, 100);
  }, []);

  // TTS playback
  const speakText = useCallback(async (text, msgIdx) => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (playingMsgIdx === msgIdx) {
      setPlayingMsgIdx(null);
      return;
    }
    const cleanText = text.replace(/[#*_`\[\]()>]/g, '').replace(/\n+/g, '. ').trim();
    if (!cleanText) return;
    setTtsLoading(msgIdx);
    try {
      const res = await axios.post(`${API}/ai/text-to-speech`, { text: cleanText }, { headers });
      const audioSrc = `data:audio/mp3;base64,${res.data.audio_base64}`;
      const audio = new Audio(audioSrc);
      audioRef.current = audio;
      setPlayingMsgIdx(msgIdx);
      setTtsLoading(null);
      audio.play();
      audio.onended = () => { setPlayingMsgIdx(null); audioRef.current = null; };
      audio.onerror = () => { setPlayingMsgIdx(null); audioRef.current = null; toast.error('Erro ao reproduzir audio'); };
    } catch {
      setTtsLoading(null);
      toast.error('Erro ao gerar voz');
    }
  }, [headers, playingMsgIdx]);

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingMsgIdx(null);
  }, []);

  // Load quick stats
  useEffect(() => {
    axios.get(`${API}/ai/quick-stats`, { headers }).then(r => setStats(r.data)).catch(() => {});
  }, [token]);

  // Load sessions list
  const loadSessions = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/ai/history`, { headers });
      setSessions(r.data);
    } catch {}
  }, [token]);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  // Load session messages
  const loadSession = useCallback(async (sid) => {
    try {
      const r = await axios.get(`${API}/ai/history?session_id=${sid}`, { headers });
      setMessages(r.data);
      setSessionId(sid);
      setShowSessions(false);
      scrollToBottom();
    } catch { toast.error('Erro ao carregar conversa'); }
  }, [token, scrollToBottom]);

  const newSession = () => {
    setSessionId(crypto.randomUUID());
    setMessages([]);
    inputRef.current?.focus();
  };

  const deleteSession = async (sid) => {
    try {
      await axios.delete(`${API}/ai/session/${sid}`, { headers });
      setSessions(prev => prev.filter(s => s.session_id !== sid));
      if (sid === sessionId) newSession();
      toast.success('Conversa excluida');
    } catch { toast.error('Erro ao excluir'); }
  };

  const sendMessage = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput('');

    const userMsg = { role: 'user', content: msg, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    scrollToBottom();
    setLoading(true);

    try {
      const r = await axios.post(`${API}/ai/chat`, { message: msg, session_id: sessionId }, { headers });
      const assistantMsg = {
        role: 'assistant',
        content: r.data.response,
        action: r.data.pending_action || null,
        action_description: r.data.action_description || null,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, assistantMsg]);
      setSessionId(r.data.session_id);
      loadSessions();
      // Auto-speak response if enabled
      if (autoSpeak && r.data.response) {
        const newMsgIdx = messages.length + 1;
        setTimeout(() => speakText(r.data.response, newMsgIdx), 200);
      }
    } catch (err) {
      const errMsg = { role: 'assistant', content: 'Desculpe, ocorreu um erro. Tente novamente.', timestamp: new Date().toISOString() };
      setMessages(prev => [...prev, errMsg]);
    } finally {
      setLoading(false);
      scrollToBottom();
      inputRef.current?.focus();
    }
  };

  const toggleVoice = async () => {
    if (isRecording) {
      mediaRecRef.current?.stop();
      setIsRecording(false);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      const chunks = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunks, { type: 'audio/webm' });
        const formData = new FormData();
        formData.append('audio', blob, 'voice.webm');
        try {
          const r = await axios.post(`${API}/ai/voice-to-text`, formData, { headers });
          if (r.data.text) { setInput(r.data.text); sendMessage(r.data.text); }
        } catch { toast.error('Erro ao processar voz'); }
      };
      recorder.start();
      mediaRecRef.current = recorder;
      setIsRecording(true);
      toast.info('Gravando voz...');
    } catch { toast.error('Microfone nao disponivel'); }
  };

  const confirmAction = async (action) => {
    setConfirming(true);
    try {
      const r = await axios.post(`${API}/ai/execute`, { action, session_id: sessionId }, { headers });
      const sysMsg = {
        role: 'system',
        content: r.data.message,
        action_result: r.data,
        timestamp: new Date().toISOString()
      };
      // Mark the action as executed in messages
      setMessages(prev => prev.map(m =>
        m.action && !m.action_result ? { ...m, action_result: r.data } : m
      ));
      setMessages(prev => [...prev, sysMsg]);
      if (r.data.success) toast.success(r.data.message);
      else toast.error(r.data.message);
    } catch { toast.error('Erro ao executar acao'); }
    finally { setConfirming(false); scrollToBottom(); }
  };

  const rejectAction = () => {
    setMessages(prev => prev.map(m =>
      m.action && !m.action_result ? { ...m, action: null } : m
    ));
    toast.info('Acao cancelada');
  };

  const quickActions = [
    { icon: BarChart3, label: 'Resumo Executivo', prompt: 'Faca um resumo executivo completo: projetos ativos, equipe, voos do mes, pilotos online, alertas de documentacao, videos. Destaque problemas que precisam de atencao imediata.', color: 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100' },
    { icon: AlertTriangle, label: 'Anomalias', prompt: 'Verifique TODAS as anomalias e problemas: documentos de drones vencidos ou vencendo, pilotos inativos, projetos sem atividade, equipe incompleta. Sugira acoes corretivas para cada problema.', color: 'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100' },
    { icon: Clock, label: 'KPIs de Voo', prompt: 'Analise completa dos KPIs de voo: total, KM, taxa de realizacao, ranking de pilotos mais ativos, comparativo semanal vs mensal.', color: 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100' },
    { icon: Users, label: 'Gestao de Equipe', prompt: 'Analise completa da equipe: todos os usuarios por projeto e papel. Quem esta online? Algum projeto com equipe incompleta ou sem piloto?', color: 'bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100' },
    { icon: Plane, label: 'Conformidade Drones', prompt: 'Status de conformidade de TODOS os drones: documentacao ANAC, seguros, datas de vencimento. Priorize por urgencia.', color: 'bg-cyan-50 border-cyan-200 text-cyan-700 hover:bg-cyan-100' },
    { icon: FolderKanban, label: 'Regulamentacao ICA 100-40', prompt: 'Quais sao as principais regras da ICA 100-40 para operacao de drones? Resuma as categorias de operacao, prazos SARPAS e restricoes de espaco aereo.', color: 'bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100' },
    { icon: Film, label: 'Manutencao Drones', prompt: 'Qual o cronograma de manutencao recomendado para os drones DJI Matrice 30 e Mavic 3E/3T? Inclua checklist pre-voo e pecas de desgaste.', color: 'bg-pink-50 border-pink-200 text-pink-700 hover:bg-pink-100' },
    { icon: Shield, label: 'Criar Usuario', prompt: 'Preciso criar um novo usuario. Me pergunte: nome completo, nome de usuario, papel (admin/supervisor/piloto/lider/vigilante) e projeto.', color: 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100' },
  ];

  const hasMessages = messages.length > 0;

  return (
    <div className="h-[calc(100vh-80px)] flex" data-testid="ai-assistant-page">
      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 bg-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gold/20 to-gold/5 border border-gold/30 flex items-center justify-center">
              <Bot size={20} className="text-gold" />
            </div>
            <div>
              <h2 className="text-base font-rajdhani font-semibold text-navy uppercase tracking-wider">ASSISTENTE IA</h2>
              <p className="text-gray-400 text-[10px] font-mono">Gestao inteligente da plataforma RPAS</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setAutoSpeak(prev => !prev); if (autoSpeak) stopAudio(); }}
              className={`text-xs h-8 ${autoSpeak ? 'border-gold text-gold bg-gold/10' : 'border-gray-200 text-gray-500'}`}
              data-testid="auto-speak-btn"
            >
              {autoSpeak ? <Volume2 size={12} className="mr-1.5" /> : <VolumeX size={12} className="mr-1.5" />}
              {autoSpeak ? 'VOZ ON' : 'VOZ OFF'}
            </Button>
            <Button variant="outline" size="sm" onClick={() => { loadSessions(); setShowSessions(true); }}
              className="text-xs h-8 border-gray-200" data-testid="sessions-btn">
              <MessageSquare size={12} className="mr-1.5" /> CONVERSAS
            </Button>
            <Button size="sm" onClick={newSession}
              className="bg-navy hover:bg-navy/90 text-white text-xs h-8" data-testid="new-chat-btn">
              <Plus size={12} className="mr-1.5" /> NOVA
            </Button>
          </div>
        </div>

        {/* Quick Stats Bar */}
        {stats && (
          <div className="flex items-center gap-4 px-6 py-2 border-b border-gray-100 bg-gray-50/50 overflow-x-auto">
            {[
              { icon: FolderKanban, label: 'Projetos', value: stats.projects, color: 'text-blue-600' },
              { icon: Users, label: 'Usuarios', value: stats.users, color: 'text-purple-600' },
              { icon: Plane, label: 'Drones', value: stats.drones, color: 'text-cyan-600' },
              { icon: Clock, label: 'Voos/Mes', value: stats.flights_month, color: 'text-green-600' },
              { icon: Film, label: 'Videos', value: stats.videos, color: 'text-pink-600' },
              { icon: Zap, label: 'Online', value: stats.pilots_online, color: 'text-emerald-600' },
              { icon: AlertTriangle, label: 'Alertas', value: stats.expiring_docs, color: stats.expiring_docs > 0 ? 'text-red-600' : 'text-gray-400' },
            ].map(s => (
              <div key={s.label} className="flex items-center gap-1.5 text-[10px] font-mono whitespace-nowrap" data-testid={`stat-${s.label.toLowerCase()}`}>
                <s.icon size={11} className={s.color} />
                <span className="text-gray-500">{s.label}:</span>
                <span className={`font-bold ${s.color}`}>{s.value}</span>
              </div>
            ))}
          </div>
        )}

        {/* Messages / Welcome */}
        <ScrollArea className="flex-1 px-6" ref={scrollRef}>
          {!hasMessages ? (
            <div className="flex flex-col items-center justify-center h-full py-12">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-gold/20 to-gold/5 border border-gold/30 flex items-center justify-center mb-6">
                <Bot size={32} className="text-gold" />
              </div>
              <h3 className="text-navy font-rajdhani font-semibold text-lg uppercase tracking-wider mb-2">
                Como posso ajudar?
              </h3>
              <p className="text-gray-400 text-xs font-mono mb-8 text-center max-w-md">
                Pergunte sobre a plataforma, gere relatorios, detecte anomalias ou execute acoes administrativas
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl w-full">
                {quickActions.map(qa => (
                  <QuickAction key={qa.label} {...qa} onClick={sendMessage} />
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              {messages.map((msg, i) => (
                <ChatMessage
                  key={i}
                  msg={msg}
                  msgIdx={i}
                  onConfirm={confirmAction}
                  onReject={rejectAction}
                  confirming={confirming}
                  onSpeak={speakText}
                  playingMsgIdx={playingMsgIdx}
                  ttsLoading={ttsLoading}
                />
              ))}
              {loading && (
                <div className="flex gap-3 justify-start animate-fade-in">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-gold/20 to-gold/5 border border-gold/30 flex items-center justify-center">
                    <Bot size={14} className="text-gold" />
                  </div>
                  <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                    <div className="flex items-center gap-2">
                      <Loader2 size={14} className="text-gold animate-spin" />
                      <span className="text-gray-400 text-xs font-mono">Analisando...</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </ScrollArea>

        {/* Quick Actions (when in conversation) */}
        {hasMessages && (
          <div className="px-6 py-2 border-t border-gray-100 bg-gray-50/30 overflow-x-auto">
            <div className="flex gap-2">
              {quickActions.map(qa => (
                <button
                  key={qa.label}
                  onClick={() => sendMessage(qa.prompt)}
                  disabled={loading}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-200 bg-white hover:bg-gray-50 text-[10px] font-medium text-gray-600 whitespace-nowrap transition-colors"
                >
                  <qa.icon size={10} /> {qa.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <div className="px-6 py-4 border-t border-gray-200 bg-white">
          <form onSubmit={(e) => { e.preventDefault(); sendMessage(); }} className="flex items-center gap-3">
            <Input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Pergunte algo ou de um comando..."
              className="flex-1 h-11 rounded-xl border-gray-200 focus:border-gold focus:ring-gold/20 text-sm"
              disabled={loading}
              data-testid="ai-chat-input"
            />
            <Button
              type="button"
              onClick={toggleVoice}
              className={`h-11 w-11 rounded-xl p-0 ${isRecording ? 'bg-red-500 hover:bg-red-600 text-white animate-pulse' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}
              data-testid="ai-voice-btn"
            >
              {isRecording ? <MicOff size={16} /> : <Mic size={16} />}
            </Button>
            <Button
              type="submit"
              disabled={!input.trim() || loading}
              className="bg-gold hover:bg-gold/90 text-navy h-11 w-11 rounded-xl p-0"
              data-testid="ai-send-btn"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </Button>
          </form>
        </div>
      </div>

      {/* Sessions Dialog */}
      <Dialog open={showSessions} onOpenChange={setShowSessions}>
        <DialogContent className="bg-white border border-gray-200 max-w-md max-h-[70vh]">
          <DialogHeader>
            <DialogTitle className="text-navy font-rajdhani uppercase tracking-wider flex items-center gap-2">
              <MessageSquare size={16} className="text-gold" /> CONVERSAS ANTERIORES
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 mt-4 max-h-[50vh] overflow-y-auto">
            {sessions.length === 0 ? (
              <p className="text-gray-400 text-xs font-mono text-center py-4">Nenhuma conversa salva</p>
            ) : (
              sessions.map(s => (
                <div key={s.session_id} className="flex items-center gap-2 p-3 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors group">
                  <button onClick={() => loadSession(s.session_id)} className="flex-1 text-left">
                    <p className="text-navy text-sm font-medium truncate">{s.preview || 'Conversa'}</p>
                    <p className="text-gray-400 text-[10px] font-mono">
                      {s.messages} msgs | {s.updated && new Date(s.updated).toLocaleDateString('pt-BR')}
                    </p>
                  </button>
                  <Button variant="ghost" size="icon" onClick={() => deleteSession(s.session_id)}
                    className="h-7 w-7 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Trash2 size={12} />
                  </Button>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AIAssistantPage;
